/**
 * serviceWorkerRegistration.js - Service worker registration and management
 *
 * Handles Progressive Web App (PWA) service worker registration for TimeGenie:
 * - Registers service worker for offline capabilities and caching
 * - Manages service worker updates and lifecycle events
 * - Provides callbacks for update notifications and success states
 * - Handles localhost vs production registration differences
 * - Includes comprehensive error handling and logging
 *
 * This lets the app load faster on subsequent visits and provides offline capabilities.
 * Users will see deployed updates after closing all tabs, since cached resources
 * are updated in the background.
 */

/**
 * Service worker registration config
 * @typedef {Object} SWConfig
 * @property {(reg?: ServiceWorkerRegistration) => void} [onSuccess] - Called when SW successfully registers
 * @property {(reg?: ServiceWorkerRegistration) => void} [onUpdate] - Called when an SW update is installed/available
 */
import logger from './utils/logger';

// Detect if running on localhost for development vs production behavior
// Covers localhost, IPv6 localhost [::1], and 127.x.x.x IP range
const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
);

/**
 * register - Main service worker registration function
 *
 * @param {SWConfig} [config] - Configuration object with callbacks
 *
 * Handles the complete service worker registration process:
 * - Validates browser support and environment
 * - Sets up update detection listeners
 * - Chooses appropriate registration method (localhost vs production)
 */
export function register(config) {
  logger.info('[SW Registration] Starting service worker registration...');
  logger.info(`[SW Registration] Environment: ${process.env.NODE_ENV}`);

  // Only register service worker in production to avoid dev cache/chunk issues
  const shouldRegister = process.env.NODE_ENV === 'production';

  if (shouldRegister) {
    if (!('serviceWorker' in navigator)) {
      logger.error('[SW Registration] Service workers not supported');
      return Promise.reject(new Error('Service workers not supported'));
    }

    logger.info('[SW Registration] Service worker support detected');

    // Validate public URL
    const publicUrl = new URL(process.env.PUBLIC_URL || '/', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      logger.error('[SW Registration] Public URL origin mismatch');
      return Promise.reject(new Error('Public URL origin mismatch'));
    }

    // Register immediately, don't wait for load event
    const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
    logger.info(`[SW Registration] SW URL: ${swUrl}`);

    // Set up service worker update detection
    // Track if we already had a controller to distinguish updates from first install
    let hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      logger.info('[SW Registration] New service worker activated');
      // Only call onUpdate if there was already a controller (not on first install)
      if (hadController && config && config.onUpdate) {
        config.onUpdate();
      }
      hadController = true;
    });

    if (isLocalhost) {
      return checkValidServiceWorker(swUrl, config);
    } else {
      return registerValidSW(swUrl, config);
    }
  } else {
    logger.warn('[SW Registration] Service worker registration skipped');
    return Promise.resolve();
  }
}

/**
 * registerValidSW - Registers service worker for production environment
 *
 * @param {string} swUrl - URL to the service worker file
 * @param {SWConfig} [config] - Configuration object with callbacks
 *
 * Handles production service worker registration:
 * - Registers SW with full scope
 * - Sets up update detection and state change listeners
 * - Calls appropriate callbacks for success/update events
 * - Provides detailed error logging and development guidance
 */
function registerValidSW(swUrl, config) {
  logger.info(`[Service Worker] Attempting to register service worker at: ${swUrl}`);

  navigator.serviceWorker
    .register(swUrl, { scope: '/' })
    .then((registration) => {
      logger.info('[Service Worker] Registration successful, scope is:', registration.scope);

      // Add event listener for controllerchange
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        logger.info('[Service Worker] Controller changed');
      });

      registration.onupdatefound = () => {
        logger.info('[Service Worker] Update found');
        const installingWorker = registration.installing;

        if (installingWorker === null) {
          logger.info('[Service Worker] No installing worker found');
          return;
        }

        logger.info(`[Service Worker] Installing worker state: ${installingWorker.state}`);

        installingWorker.onstatechange = () => {
          logger.info(
            `[Service Worker] Service worker state changed to: ${installingWorker.state}`,
          );

          if (installingWorker.state === 'installed') {
            logger.info('[Service Worker] Service worker installed');

            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              logger.info(
                '[Service Worker] New content is available and will be used when all tabs for this page are closed.',
              );

              // Execute callback
              if (config && config.onUpdate) {
                logger.info('[Service Worker] Calling onUpdate callback');
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              logger.info('[Service Worker] Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                logger.info('[Service Worker] Calling onSuccess callback');
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      // Check if the service worker is controlling the page
      if (navigator.serviceWorker.controller) {
        logger.info('[Service Worker] This web app is being controlled by a service worker');
      } else {
        logger.info('[Service Worker] This web app is not yet controlled by a service worker');
      }
    })
    .catch((error) => {
      logger.error('[Service Worker] Registration failed with error:', error);

      // Additional error information
      if (error instanceof Error) {
        logger.error('[Service Worker] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
      }

      // If we're in development, provide more specific guidance
      if (process.env.NODE_ENV === 'development') {
        logger.error(
          '[Service Worker] Development mode: Make sure you are serving the app over HTTPS or localhost',
        );
        logger.error(
          '[Service Worker] If using a custom domain, ensure the service worker file is accessible',
        );
      }
    });
}

/**
 * checkValidServiceWorker - Validates service worker for localhost environment
 *
 * @param {string} swUrl - URL to the service worker file
 * @param {SWConfig} [config] - Configuration object with callbacks
 *
 * Development-specific service worker validation:
 * - Fetches SW file to verify it exists and is valid JavaScript
 * - Handles 404 errors by cleaning up registrations and reloading
 * - Provides offline mode detection and error handling
 */
function checkValidServiceWorker(swUrl, config) {
  logger.info(`[Service Worker] Checking service worker at: ${swUrl}`);

  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
    cache: 'no-store', // Ensure we don't get a cached response
  })
    .then((response) => {
      logger.info(
        `[Service Worker] Service worker fetch response: ${response.status} ${response.statusText}`,
      );

      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      logger.info(`[Service Worker] Content-Type: ${contentType}`);

      if (
        response.status === 404 ||
        (contentType !== null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        logger.error(
          '[Service Worker] No service worker found at the expected path or invalid content type',
        );

        if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            logger.info(
              `[Service Worker] Found ${registrations.length} service worker registrations`,
            );
            registrations.forEach((registration) => {
              logger.info(`[Service Worker] Unregistering service worker: ${registration.scope}`);
              registration.unregister().then(() => {
                logger.info('[Service Worker] Successfully unregistered service worker');
                window.location.reload();
              });
            });
          });
        } else {
          window.location.reload();
        }
      } else {
        // Service worker found. Proceed as normal.
        logger.info('[Service Worker] Service worker found and valid');
        registerValidSW(swUrl, config);
      }
    })
    .catch((error) => {
      logger.error('[Service Worker] Error checking service worker:', error);
      logger.info(
        'No internet connection found or service worker not accessible. App is running in offline mode.',
      );
    });
}

/**
 * unregister - Unregisters the service worker
 *
 * Completely removes the service worker registration:
 * - Waits for service worker to be ready
 * - Calls unregister() on the registration
 * - Handles any errors during unregistration
 *
 * Used for debugging or when disabling PWA functionality
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        logger.error(error.message);
      });
  }
}
